'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Text } from 'ui/src/components/Text/Text'
import { type FlowProps } from '../flows.types'
import { ForeverPieChart } from './ForeverPieChart'
import {
  buttonContainer,
  dot,
  fakeButton,
  friendLeft,
  friendRow,
  friendsContainer,
  header,
  pieLabel,
  pieWrapper,
  root,
} from './Flow05Forever.css'

type Friend = {
  name: string
}

const FRIENDS: Array<Friend> = [{ name: 'Elin' }, { name: 'Hampus' }, { name: 'Li' }, { name: 'Peter' }]

const DISCOUNT_AMOUNTS = [10, 20, 30, 40] as const

const TOTAL_PREMIUM = 179
const PER_FRIEND_DISCOUNT = 10

// Reveal cadence: friend 1 is visible at mount; 2/3/4 join at these timestamps (ms).
const FRIEND_REVEAL_TIMINGS = [500, 2000, 3500] as const

// onComplete is part of the shared FlowProps contract but Flow05 is timer-driven
// by the orchestrator, so it is intentionally unused here.
export function Flow05Forever(_props: FlowProps) {
  const { t } = useTranslation('appShowcase')
  const [visibleFriends, setVisibleFriends] = useState(1)

  useEffect(() => {
    const timeouts = FRIEND_REVEAL_TIMINGS.map((delay, i) => setTimeout(() => setVisibleFriends(i + 2), delay))
    return () => timeouts.forEach(clearTimeout)
  }, [])

  const visible = FRIENDS.slice(0, visibleFriends)
  const totalAmount = DISCOUNT_AMOUNTS[visibleFriends - 1]
  const totalLabel = t('FLOW_05_DISCOUNT_TOTAL', { amount: totalAmount })

  return (
    <div className={root}>
      <div className={header}>
        <Text as="span" size="sm" color="textPrimary">
          {t('FLOW_05_HEADER')}
        </Text>
      </div>

      <div className={pieWrapper}>
        <ForeverPieChart discount={visibleFriends * PER_FRIEND_DISCOUNT} total={TOTAL_PREMIUM} />
        <div className={pieLabel}>
          <Text as="p" size={{ _: 'xxs', md: 'xs' }} color="textPrimary">
            {t('FLOW_05_DISCOUNT_LABEL')}
          </Text>
          <AnimatePresence mode="wait">
            <motion.div
              key={totalLabel}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Text as="p" size={{ _: 'xxs', md: 'xs' }} color="textSecondary">
                {totalLabel}
              </Text>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className={friendsContainer}>
        {visible.map((friend) => (
          <motion.div key={friend.name} layout transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              className={friendRow}
            >
              <div className={friendLeft}>
                <div className={dot} />
                <Text as="span" size={{ _: 'xxs', md: 'xs' }} color="textPrimary">
                  {friend.name}
                </Text>
              </div>
              <Text as="span" size={{ _: 'xxs', md: 'xs' }} color="textPrimary">
                {t('FLOW_05_FRIEND_DISCOUNT', { amount: PER_FRIEND_DISCOUNT })}
              </Text>
            </motion.div>
          </motion.div>
        ))}
      </div>

      <div className={buttonContainer}>
        <Text as="div" align="center" className={fakeButton} size={{ _: 'xxs', md: 'xs' }} color="textPrimary">
          {t('FLOW_05_INVITE_BUTTON')}
        </Text>
      </div>
    </div>
  )
}
